import React, { BaseSyntheticEvent, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  IonGrid,
  IonItemDivider,
  IonLabel,
  IonToggle,
  IonItem,
  IonRow,
  IonCol,
  IonList,
  IonSpinner,
} from '@ionic/react'
import {
  getImportSelectionsSelectedAttachments,
  getPulsaraDetailsAttachmentList,
  getPulsaraLoading,
} from '../../selectors'
import PulsaraAttachmentRow from './PulsaraAttachmentRow.component'
import { setSelectedAttachments } from '../../store/pulsara.actions'
import { IPulsaraAttachmentItem } from '../../store/pulsara.types'
import './PulsaraImport.component.css'
import './PulsaraAttachments.component.css'

export interface IPulsaraAttachmentColumn {
  key: keyof IPulsaraAttachmentItem
  label: string
  isDescription?: boolean
}

const ATTACHMENTS_COLUMNS: IPulsaraAttachmentColumn[] = [
  { key: 'name', label: 'Name', isDescription: true },
  { key: 'category', label: 'Category' },
]

const PulsaraAttachments: React.FC = () => {
  const { t } = useTranslation()
  const attachmentList = useSelector(getPulsaraDetailsAttachmentList)
  const selectedAttachments = useSelector(getImportSelectionsSelectedAttachments)
  const loading = useSelector(getPulsaraLoading)
  const dispatch = useDispatch()

  const attachmentsFetching = loading === 'attachments'
  const hasAttachments = attachmentList != null && attachmentList.length > 0

  const isSelected = useCallback(
    (id: number) => selectedAttachments?.find((a: IPulsaraAttachmentItem) => a.id === id) !== undefined || false,
    [selectedAttachments]
  )

  const onToggleItem = (checked: boolean, item: IPulsaraAttachmentItem) => {
    let dataToImport = selectedAttachments ? [...selectedAttachments] : []
    if (checked) {
      dataToImport.push(item)
    } else {
      dataToImport = dataToImport.filter((a: IPulsaraAttachmentItem) => a.id !== item.id)
    }
    dispatch(setSelectedAttachments(dataToImport))
  }

  const onToggleAll = (e: BaseSyntheticEvent) => {
    const checked = e.target.checked
    if (checked) {
      dispatch(setSelectedAttachments(attachmentList))
    } else {
      dispatch(setSelectedAttachments([]))
    }
  }

  const isAllSelected = useMemo(
    () => selectedAttachments?.length === (attachmentList?.length ?? 0),
    [selectedAttachments, attachmentList]
  )

  return (
    <>
      <IonItemDivider color='light' className='pulsara-import-divider'>
        <IonLabel className='pulsara-attachment-divider-label'>Attachments</IonLabel>
      </IonItemDivider>
      {attachmentsFetching ? (
        <div className='pulsara-import-loading'>
          <IonSpinner name='crescent' />
          <IonLabel className='ion-padding'>{t('pulsara.loading.attachments')}</IonLabel>
        </div>
      ) : !hasAttachments ? (
        <div className='pulsara-import-empty'>
          <IonLabel color='medium'>{t('pulsara.NoAttachmentsAvailable')}</IonLabel>
        </div>
      ) : (
        <IonList lines='full' className='pulsara-attachment-list'>
          <IonItem className='pulsara-attachment-header-item pulsara-attachment-main'>
            <IonToggle slot='start' checked={isAllSelected} onClick={onToggleAll} />
            <IonGrid className='pulsara-attachment-grid'>
              <IonRow className='pulsara-attachment-header-row'>
                {ATTACHMENTS_COLUMNS.map((column) => (
                  <IonCol
                    key={column.key}
                    className={`pulsara-attachment-column pulsara-attachment-column-${column.key} ${
                      column.isDescription ? 'pulsara-attachment-description' : ''
                    }`}>
                    <IonLabel>
                      <h3>{column.label}</h3>
                    </IonLabel>
                  </IonCol>
                ))}
                <IonCol className='pulsara-attachment-more'></IonCol>
              </IonRow>
            </IonGrid>
          </IonItem>
          <hr className='pulsara-attachment-first-divider' />
          {attachmentList?.map((row: IPulsaraAttachmentItem) => (
            <React.Fragment key={row.id}>
              <PulsaraAttachmentRow
                row={row}
                isSelected={isSelected(row.id)}
                columns={ATTACHMENTS_COLUMNS}
                onToggleItem={onToggleItem}
              />
            </React.Fragment>
          ))}
        </IonList>
      )}
    </>
  )
}

export default PulsaraAttachments
